import { SearchOutlined, SyncOutlined } from "@ant-design/icons";
import {
  Row,
  Col,
  Typography,
  Select,
  Input,
  Button,
  Space,
  Form,
  Table,
  Flex,
} from "antd";
import { useEffect, useState } from "react";
import "../Pages/CSS/Traitement.css";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { JournalCentralService } from "../Services/JournalCentralService";
import { SuspensionsService } from "../Services/SuspensionsService";
import { TraitementService } from "../Services/TraitementService";

const { Option } = Select;

const ChargesIPM = () => {
  const getSelectedYear = () => new Date().getFullYear().toString();

  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getSelectedYear());
  const [years, setYears] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalAmount, setTotalAmount] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  const [form] = Form.useForm();

  const journalCentralService = new JournalCentralService();
  const suspensionsService = new SuspensionsService();
  const traitementsoinsService = new TraitementService();

  useEffect(() => {
    const fetchDataInit = async () => {
      const getParam = await traitementsoinsService.getParamComptable();
      const yearFromParamData = dayjs(getParam.datedeb).format("YYYY");
      setSelectedYear(yearFromParamData);
    };
    fetchDataInit();
  }, []);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const yearsData = await journalCentralService.getComptaExercices();
        setYears([...yearsData].sort((a, b) => b - a));
      } catch (error) {
        console.error("Erreur lors de la récupération des exercices :", error);
      }
    };
    fetchYears();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedYear) {
        setLoading(true);
        try {
          const data = await suspensionsService.getChargesIPM(selectedYear);
          setTableData(data);
        } catch (error) {
          console.error("Erreur lors de la récupération des charges IPM :", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    if (!globalSearch.trim()) {
      setFilteredData(tableData);
    } else {
      const lower = globalSearch.toLowerCase();
      setFilteredData(
        tableData.filter(row =>
          Object.values(row).some(val =>
            val?.toString().toLowerCase().includes(lower)
          )
        )
      );
    }
  }, [globalSearch, tableData]);

  useEffect(() => {
    const total = filteredData.reduce(
      (acc, curr) => acc + parseFloat(curr.montant || 0),
      0
    );
    setTotalAmount(total);
    setRowCount(filteredData.length);
  }, [filteredData]);

  const handleYearChange = (value) => setSelectedYear(value);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      if (selectedYear) {
        const data = await suspensionsService.getChargesIPM(selectedYear);
        setTableData(data);
      }
    } catch (error) {
      console.error("Erreur lors de l'actualisation :", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (data, fileName = "charges_ipm.xlsx") => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, fileName);
  };

  const columns = [
    {
      title: 'Matricule',
      dataIndex: 'numcont',
      key: 'numcont',
      align: "center",
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher matricule"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block', width: 200 }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
              Chercher
            </Button>
            <Button onClick={() => { clearFilters && clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
      onFilter: (value, record) => record.numcont?.toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) => {
        if (!a.numcont && b.numcont) return 1;
        if (a.numcont && !b.numcont) return -1;
        if (!a.numcont && !b.numcont) return 0;
        return a.numcont.localeCompare(b.numcont);
      },
    },
    {
      title: 'Participant',
      dataIndex: 'part',
      key: 'part',
      sorter: (a, b) => {
        if (!a.part && b.part) return 1;
        if (a.part && !b.part) return -1;
        if (!a.part && !b.part) return 0;
        return a.part.localeCompare(b.part);
      },
    },
    {
      title: 'Sexe',
      dataIndex: 'sexe',
      key: 'sexe',
      align: "center",
      filters: [...new Set(tableData.map(r => r.sexe).filter(Boolean))].map(v => ({ text: v, value: v })),
      onFilter: (value, record) => record.sexe === value,
      sorter: (a, b) => {
        if (!a.sexe && b.sexe) return 1;
        if (a.sexe && !b.sexe) return -1;
        if (!a.sexe && !b.sexe) return 0;
        return a.sexe.localeCompare(b.sexe);
      },
    },
    {
      title: 'Société',
      dataIndex: 'societe',
      key: 'societe',
      filters: [...new Set(tableData.map(r => r.societe).filter(Boolean))].sort().map(v => ({ text: v, value: v })),
      onFilter: (value, record) => record.societe === value,
      sorter: (a, b) => {
        if (!a.societe && b.societe) return 1;
        if (a.societe && !b.societe) return -1;
        if (!a.societe && !b.societe) return 0;
        return a.societe.localeCompare(b.societe);
      },
    },
    {
      title: 'Âge',
      dataIndex: 'agepart',
      key: 'agepart',
      align: "center",
      filters: [
        { text: '< 25 ans', value: '0-24' },
        { text: '25 - 35 ans', value: '25-35' },
        { text: '36 - 45 ans', value: '36-45' },
        { text: '46 - 55 ans', value: '46-55' },
        { text: '> 55 ans', value: '56+' },
      ],
      onFilter: (value, record) => {
        const age = record.agepart ?? 0;
        if (value === '0-24') return age < 25;
        if (value === '25-35') return age >= 25 && age <= 35;
        if (value === '36-45') return age >= 36 && age <= 45;
        if (value === '46-55') return age >= 46 && age <= 55;
        if (value === '56+') return age > 55;
        return false;
      },
      sorter: (a, b) => (a.agepart ?? 0) - (b.agepart ?? 0),
    },
    {
      title: 'N° téléphone',
      dataIndex: 'portable',
      key: 'portable',
      sorter: (a, b) => {
        if (!a.portable && b.portable) return 1;
        if (a.portable && !b.portable) return -1;
        if (!a.portable && !b.portable) return 0;
        return a.portable.localeCompare(b.portable);
      },
    },
    {
      title: 'Seuil',
      dataIndex: 'seuil_charges_ipm',
      key: 'seuil_charges_ipm',
      align: "right",
      onHeaderCell: () => ({ style: { backgroundColor: '#e6f4ff' } }),
      onCell: () => ({ style: { backgroundColor: '#e6f4ff' } }),
      sorter: (a, b) => (parseFloat(a.seuil_charges_ipm) || 0) - (parseFloat(b.seuil_charges_ipm) || 0),
      render: (text) => (
        <div style={{ paddingRight: 10 }}>
          <strong>
            {text && parseFloat(text) !== 0 ? parseFloat(text).toLocaleString() : ""}
          </strong>
        </div>
      ),
    },
    {
      title: 'Montant',
      dataIndex: 'conso',
      key: 'conso',
      align: "right",
      onHeaderCell: () => ({ style: { backgroundColor: '#fff7e6' } }),
      onCell: () => ({ style: { backgroundColor: '#fff7e6' } }),
      filters: [
        { text: '= 0', value: '0' },
        { text: '< 50 000', value: '1-50000' },
        { text: '50 000 - 200 000', value: '50001-200000' },
        { text: '200 000 - 500 000', value: '200001-500000' },
        { text: '> 500 000', value: '500001+' },
      ],
      onFilter: (value, record) => {
        const conso = parseFloat(record.conso) || 0;
        if (value === '0') return conso === 0;
        if (value === '1-50000') return conso > 0 && conso <= 50000;
        if (value === '50001-200000') return conso > 50000 && conso <= 200000;
        if (value === '200001-500000') return conso > 200000 && conso <= 500000;
        if (value === '500001+') return conso > 500000;
        return false;
      },
      sorter: (a, b) => (parseFloat(a.conso) || 0) - (parseFloat(b.conso) || 0),
      render: (text) => (
        <div style={{ paddingRight: 10 }}>
          <strong>
            {text && parseFloat(text) !== 0 ? parseFloat(text).toLocaleString() : ""}
          </strong>
        </div>
      ),
    },
    {
      title: 'Écart',
      key: 'ecart',
      align: "right",
      sorter: (a, b) => {
        const ecartA = (parseFloat(a.conso) || 0) - (parseFloat(a.seuil_charges_ipm) || 0);
        const ecartB = (parseFloat(b.conso) || 0) - (parseFloat(b.seuil_charges_ipm) || 0);
        return ecartA - ecartB;
      },
      render: (_, record) => {
        const montant = parseFloat(record.conso) || 0;
        const seuil = parseFloat(record.seuil_charges_ipm) || 0;
        const ecart = montant - seuil;
        if (ecart === 0 || seuil === 0) return null;
        const color = ecart > 0 ? '#cf1322' : '#389e0d';
        return (
          <div style={{ paddingRight: 10, color, fontWeight: 600 }}>
            {ecart > 0 ? '+' : ''}{ecart.toLocaleString()}
          </div>
        );
      },
    },
    {
      title: '% Dépassement',
      key: 'pct_depassement',
      align: "right",
      sorter: (a, b) => {
        const seuilA = parseFloat(a.seuil_charges_ipm) || 0;
        const seuilB = parseFloat(b.seuil_charges_ipm) || 0;
        const pctA = seuilA !== 0 ? ((parseFloat(a.conso) || 0) - seuilA) / seuilA * 100 : 0;
        const pctB = seuilB !== 0 ? ((parseFloat(b.conso) || 0) - seuilB) / seuilB * 100 : 0;
        return pctA - pctB;
      },
      render: (_, record) => {
        const montant = parseFloat(record.conso) || 0;
        const seuil = parseFloat(record.seuil_charges_ipm) || 0;
        if (seuil === 0) return null;
        const pct = ((montant - seuil) / seuil) * 100;
        const color = pct > 0 ? '#cf1322' : '#389e0d';
        return (
          <div style={{ paddingRight: 10, color, fontWeight: 600 }}>
            {pct > 0 ? '+' : ''}{pct.toFixed(2)} %
          </div>
        );
      },
    },
  ];

  const isTableEmpty = filteredData.length === 0;

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col
          xs={24} sm={24} md={12} lg={12} xl={8}
          style={{ display: "flex", justifyContent: "start" }}
        >
          <Space wrap align="center">
            <Select
              className="select-colored-accent"
              style={{
                width: 120,
                "--select-accent": "#1890ff",
                "--select-accent-bg": "#e6f4ff",
                "--select-accent-shadow": "rgba(24, 144, 255, 0.25)",
              }}
              onChange={handleYearChange}
              value={selectedYear}
            >
              {years.map((year) => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
            <Input
              className="search-input-highlight"
              placeholder="Rechercher..."
              allowClear
              prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
              style={{ width: 250 }}
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
            />
          </Space>
        </Col>
        <Col
          xs={24} sm={24} md={12} lg={12} xl={16}
          style={{ display: "flex", justifyContent: "end", alignItems: "end" }}
        >
          <Flex wrap="wrap" gap="small">
            <Button
              className="buttonExport"
              disabled={isTableEmpty}
              onClick={() => exportToExcel(filteredData)}
            >
              Export Excel
            </Button>
            <Button
              icon={<SyncOutlined />}
              className="buttonRefresh"
              onClick={handleRefresh}
              loading={loading}
            >
              Actualiser
            </Button>
          </Flex>
        </Col>
      </Row>

      <Table
        scroll={{ x: true }}
        style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
        className="custom-header2-table"
        pagination={{
          pageSizeOptions: ["5", "10", "20", "50", "100"],
          showSizeChanger: true,
          pageSize: pageSize,
          onShowSizeChange: (current, size) => setPageSize(size),
        }}
        bordered
        showSorterTooltip={false}
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey={record => record.numcont + (record.numdoc || '')}
        title={() => (
          <span>{rowCount} ligne{rowCount > 1 ? 's' : ''}</span>
        )}
      />
    </div>
  );
};

export default ChargesIPM;
